import { useContext, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  useCreateLeaseableResource,
  useUpdateLeaseableResource,
} from 'src/api/rpc/hooks/leaseable-resources/useLeaseableResources';
import { useCredentialsList } from 'src/api/rpc/hooks/credentials/useCredentials';
import { Autocomplete } from 'src/components/common/inputs/autocomplete';
import { pickerLabels } from 'src/components/common/inputs/pickerLabels';
import { AuthContext } from '@/context/auth.context';
import { T, Num, msg, useGT, useMessages } from 'gt-react';
import { PencilIcon } from '@phosphor-icons/react';
import { CredentialFormDialog } from '../credentials/CredentialFormDialog';
import { isCredentialTypeValue } from '../credentials/credentialFormSubmission';

/** Entity types that can be wrapped by a leasable resource gate. */
const RESOURCE_TYPE_OPTIONS = [
  { value: 'credential', label: msg('Credential') },
] as const;

type ResourceTypeValue = (typeof RESOURCE_TYPE_OPTIONS)[number]['value'];

export type LeaseableResourceFormValues = {
  id: string;
  name: string;
  resourceType: string;
  resourceId: string;
  maxConcurrent: number;
  leaseTtlSeconds: number;
  isActive: boolean;
};

interface LeaseableResourceFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, opens in edit mode pre-filled with these values. */
  resource?: LeaseableResourceFormValues;
  /** Called after a successful create with the new resource's ID. */
  onCreated?: (resourceId: string) => void;
}

export function LeaseableResourceFormDialog({
  open,
  onClose,
  resource,
  onCreated,
}: LeaseableResourceFormDialogProps) {
  const gt = useGT();
  const m = useMessages();
  const isEditMode = Boolean(resource);
  const { currentUser } = useContext(AuthContext);
  const organizationId = currentUser?.organizationId;

  const [name, setName] = useState(resource?.name ?? '');
  const [resourceType, setResourceType] = useState<ResourceTypeValue>(
    (resource?.resourceType as ResourceTypeValue) ?? 'credential',
  );

  const resourceTypeLabel = RESOURCE_TYPE_OPTIONS.find(
    (o) => o.value === resourceType,
  )?.label;
  const [resourceId, setResourceId] = useState(resource?.resourceId ?? '');
  const [editingCredential, setEditingCredential] = useState(false);
  const [maxConcurrent, setMaxConcurrent] = useState(
    String(resource?.maxConcurrent ?? 1),
  );
  // Store TTL in minutes in the form, convert to seconds on submit
  const [leaseTtlMinutes, setLeaseTtlMinutes] = useState(
    String(Math.round((resource?.leaseTtlSeconds ?? 1800) / 60)),
  );
  const [isActive, setIsActive] = useState(resource?.isActive ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { credentials } = useCredentialsList({
    organizationId,
    limit: 100,
  });

  function resetForm() {
    setName('');
    setResourceType('credential');
    setResourceId('');
    setMaxConcurrent('1');
    setLeaseTtlMinutes('30');
    setIsActive(true);
    setErrors({});
  }

  function validate(): boolean {
    const next: Record<string, string> = {};

    if (!name.trim()) {
      next.name = gt('Name is required', {
        context: 'Agent resource form validation',
      });
    } else if (name.trim().length > 255) {
      next.name = gt('Must not exceed 255 characters', {
        context: 'Agent resource form validation',
      });
    }

    if (!resourceType) {
      next.resourceType = gt('Resource type is required', {
        context: 'Agent resource form validation',
      });
    }

    if (!resourceId.trim()) {
      next.resourceId = gt('Select a resource', {
        context: 'Agent resource form validation',
      });
    }

    const parsedMax = Number(maxConcurrent);
    if (!Number.isInteger(parsedMax) || parsedMax < 1) {
      next.maxConcurrent = gt('Must be a whole number of at least 1', {
        context: 'Agent resource form validation',
      });
    }

    const parsedTtl = Number(leaseTtlMinutes);
    if (!Number.isInteger(parsedTtl) || parsedTtl < 1) {
      next.leaseTtlMinutes = gt('Must be at least 1 minute', {
        context: 'Agent resource form validation',
      });
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const createMutation = useCreateLeaseableResource({
    onSuccess: () => {
      resetForm();
      onClose();
    },
  });

  const updateMutation = useUpdateLeaseableResource({
    onSuccess: () => {
      resetForm();
      onClose();
    },
  });

  function handleSubmit() {
    if (!validate()) return;

    const parsedMax = Number(maxConcurrent);
    const parsedTtlSeconds = Number(leaseTtlMinutes) * 60;

    if (isEditMode && resource) {
      updateMutation.mutate(
        {
          resourceId: resource.id,
          name: name.trim(),
          maxConcurrent: parsedMax,
          leaseTtlSeconds: parsedTtlSeconds,
          isActive,
        },
        {
          onError: (error) => {
            setErrors({ form: error.message });
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          name: name.trim(),
          resourceType,
          resourceId: resourceId.trim(),
          maxConcurrent: parsedMax,
          leaseTtlSeconds: parsedTtlSeconds,
          isActive,
        },
        {
          onSuccess: (created) => {
            onCreated?.(created.id);
          },
          onError: (error) => {
            setErrors({ form: error.message });
          },
        },
      );
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      resetForm();
      onClose();
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Resolve the display label for the selected entity when in edit mode.
  const selectedCredential =
    resourceType === 'credential'
      ? credentials.find((c) => c.id === resourceId)
      : undefined;
  const editableCredential =
    selectedCredential && isCredentialTypeValue(selectedCredential.type)
      ? selectedCredential
      : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? (
                <T context="dialog title editing a leaseable resource">
                  Edit resource
                </T>
              ) : (
                <T context="dialog title creating a leaseable resource">
                  Add resource
                </T>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {errors.form && (
              <p className="text-sm text-destructive">{errors.form}</p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="lr-name">
                <T context="leaseable resource form field label">Name</T>
              </Label>
              <Input
                id="lr-name"
                placeholder={gt('e.g., Oracle RDP — Production', {
                  context: 'example name for a leaseable resource',
                })}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="lr-resource-type">
                <T context="leaseable resource form field label">
                  Resource type
                </T>
              </Label>
              <Select
                value={resourceType}
                onValueChange={(v) => {
                  setResourceType(v as ResourceTypeValue);
                  setResourceId('');
                }}
                disabled={isEditMode}
              >
                <SelectTrigger id="lr-resource-type">
                  <SelectValue
                    placeholder={gt('Select type...', {
                      context: 'placeholder for resource type selector',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {m(opt.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.resourceType && (
                <p className="text-xs text-destructive">
                  {errors.resourceType}
                </p>
              )}
              {isEditMode && (
                <p className="text-xs text-muted-foreground">
                  <T context="help text when resource type is locked">
                    Type cannot be changed after creation
                  </T>
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="lr-resource-id">
                {resourceTypeLabel ? (
                  m(resourceTypeLabel)
                ) : (
                  <T context="leaseable resource form field label, generic fallback">
                    Resource
                  </T>
                )}
              </Label>
              {isEditMode ? (
                <Input
                  id="lr-resource-id"
                  value={selectedCredential?.name ?? resourceId}
                  disabled
                  className={
                    selectedCredential ? undefined : 'font-mono text-xs'
                  }
                />
              ) : (
                <Autocomplete
                  deselectable
                  items={credentials}
                  value={resourceId || null}
                  getValue={(c) => c.id}
                  getLabel={(c) => c.name}
                  onValueChange={(id) => setResourceId(id ?? '')}
                  labels={pickerLabels({
                    placeholder: gt('Search credentials...', {
                      context:
                        'placeholder for credential selector in resource form',
                    }),
                    search: gt('Search by name...', {
                      context:
                        'search input placeholder for credential selector',
                    }),
                    empty: gt('No credentials found', {
                      context: 'empty state for credential selector',
                    }),
                  })}
                  indicator="none"
                  renderItem={(c, { selected: isSelected }) => (
                    <div className="flex items-center gap-2 w-full">
                      <span
                        className={
                          isSelected
                            ? 'opacity-100 text-[10px]'
                            : 'opacity-0 text-[10px]'
                        }
                      >
                        ✓
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono shrink-0">
                        {c.type}
                      </span>
                    </div>
                  )}
                  allowClear
                />
              )}
              {errors.resourceId && (
                <p className="text-xs text-destructive">{errors.resourceId}</p>
              )}
              {isEditMode && (
                <p className="text-xs text-muted-foreground">
                  <T context="help text when resource id is locked">
                    Resource cannot be changed after creation
                  </T>
                </p>
              )}
            </div>

            {selectedCredential && (
              <div className="flex flex-col gap-2 rounded-md border p-3">
                <p className="text-sm">
                  {selectedCredential.autoPauseAfterRejectedRuns == null ? (
                    <T context="Agent resource credential has automatic pausing disabled">
                      Auto-Pause Off
                    </T>
                  ) : (
                    <T context="Agent resource credential rejection threshold">
                      Auto-pause after{' '}
                      <Num>{selectedCredential.autoPauseAfterRejectedRuns}</Num>{' '}
                      consecutive rejected runs.
                    </T>
                  )}
                </p>
                {editableCredential && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingCredential(true)}
                  >
                    <PencilIcon className="size-4" />
                    <T context="Edit the shared credential policy from an agent resource">
                      Edit Credential
                    </T>
                  </Button>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="lr-max-concurrent">
                  <T context="leaseable resource form field label">
                    Max concurrent agents
                  </T>
                </Label>
                <Input
                  id="lr-max-concurrent"
                  type="number"
                  min={1}
                  step={1}
                  value={maxConcurrent}
                  onChange={(e) => setMaxConcurrent(e.target.value)}
                />
                {errors.maxConcurrent && (
                  <p className="text-xs text-destructive">
                    {errors.maxConcurrent}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lr-ttl">
                  <T context="leaseable resource form field label">
                    Lease TTL (minutes)
                  </T>
                </Label>
                <Input
                  id="lr-ttl"
                  type="number"
                  min={1}
                  step={1}
                  value={leaseTtlMinutes}
                  onChange={(e) => setLeaseTtlMinutes(e.target.value)}
                />
                {errors.leaseTtlMinutes && (
                  <p className="text-xs text-destructive">
                    {errors.leaseTtlMinutes}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Switch
                id="lr-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <Label htmlFor="lr-active" className="cursor-pointer">
                <T context="toggle label for marking a leaseable resource active">
                  Active
                </T>
              </Label>
              <p className="text-xs text-muted-foreground">
                <T context="help text for active toggle on leaseable resource">
                  Inactive resources cannot be leased by agents
                </T>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              <T context="cancel button in leaseable resource dialog">Cancel</T>
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? (
                <T context="saving state for leaseable resource dialog">
                  Saving...
                </T>
              ) : (
                <T context="save button in leaseable resource dialog">Save</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editingCredential && editableCredential && (
        <CredentialFormDialog
          open
          onClose={() => setEditingCredential(false)}
          credential={editableCredential}
        />
      )}
    </>
  );
}
